import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { Toaster as SonnerToaster, toast, type ToasterProps } from 'sonner'
import { GreenCheckIcon } from '../Icons'

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <output aria-live="polite" aria-atomic="true" style={{ all: 'unset' }}>
      <SonnerToaster
        className="toaster group h-4 !bg-transparent"
        toastOptions={{
          classNames: {
            toast:
              'rounded-xl border-transparent bg-transparent group toast group-[.toaster]:bg-transparent group-[.toaster]:text-foreground group-[.toaster]:border group-[.toaster]:shadow-none bg-transparent max-w-full radius-xl',
            description: 'group-[.toast]:text-muted-foreground',
            actionButton:
              'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
            cancelButton:
              'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          },
        }}
        {...props}
      />
    </output>
  )
}

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastConfig {
  bgColor: string
  borderColor: string
  textColor: string
  icon: React.ReactNode
}

const toastConfigs: Record<ToastType, ToastConfig> = {
  success: {
    bgColor: 'bg-ss-positive-50',
    borderColor: 'border-ss-positive-100',
    textColor: 'text-ss-neutral-950',
    icon: (
      <GreenCheckIcon className="h-4 w-4 text-ss-positive-200 flex-shrink-0" />
    ),
  },
  error: {
    bgColor: 'bg-ss-critical-50',
    borderColor: 'border-ss-critical-100',
    textColor: 'text-ss-neutral-950',
    icon: (
      <AlertCircle className="h-4 w-4 text-ss-critical-200 flex-shrink-0" />
    ),
  },
  warning: {
    bgColor: 'bg-ss-warning-50',
    borderColor: 'border-ss-warning-100',
    textColor: 'text-ss-neutral-950',
    icon: (
      <AlertTriangle className="h-4 w-4 text-ss-warning-200 flex-shrink-0" />
    ),
  },
  info: {
    bgColor: 'bg-ss-blue-50',
    borderColor: 'border-ss-blue-100',
    textColor: 'text-ss-neutral-950',
    icon: <Info className="h-4 w-4 text-ss-blue-200 flex-shrink-0" />,
  },
}

export const showToast = (
  type: ToastType,
  message: string,
  subtext?: string,
  duration: number = 3000
) => {
  const config = toastConfigs[type]

  toast.custom(
    t => (
      <div
        className={`flex items-start gap-2 ${config.bgColor} border ${config.borderColor} rounded-lg px-3 py-2 shadow-lg min-w-[510px] w-full`}
      >
        {/* <div className="flex items-center gap-2"> */}
        {config.icon}
        <div className="flex flex-col items-start w-full">
          <span
            className={`text-sm font-semibold text-ss-neutral-950 ${config.textColor} flex-1`}
          >
            {message}
          </span>
          {/* </div> */}
          <p
            className={`text-sm font-normal block text-ss-neutral-600 ${config.textColor} flex-1`}
          >
            {subtext}
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t)}
          className="flex-shrink-0 p-0.5 hover:bg-ss-neutral-100 rounded transition-colors"
        >
          <X className="h-3.5 w-3.5 text-ss-neutral-600" />
        </button>
      </div>
    ),
    {
      duration,
      position: 'top-center',
    }
  )
}

// Convenience functions for specific toast types
export const showSuccessToast = (
  message: string,
  subtext?: string,
  duration?: number
) => {
  showToast('success', message, subtext, duration)
}

export const showErrorToast = (
  message: string,
  subtext?: string,
  duration?: number
) => {
  showToast('error', message, subtext, duration)
}

export const showWarningToast = (
  message: string,
  subtext?: string,
  duration?: number
) => {
  showToast('warning', message, subtext, duration)
}

export const showInfoToast = (
  message: string,
  subtext?: string,
  duration?: number
) => {
  showToast('info', message, subtext, duration)
}

export { Toaster }
